import Box from '@mui/material/Box';
import {
  DriverDay_Read_Typeahead,
  getV1CompaniesCompanyIdJobsJobIdSiteUsages,
  getV1CompaniesCompanyIdLoadsId,
  getV1CompaniesCompanyIdLoadsLoadIdSiteUsages,
  getV1CompaniesCompanyIdOrdersOrderIdSiteUsages,
  Job_Read_Typeahead,
  Load_Read_Typeahead,
  Order_Read_Typeahead,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import AutocompleteDriverDaysTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteDriverDaysTypeahead';
import AutocompleteJobsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteJobsTypeahead';
import AutocompleteLoadsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteLoadsTypeahead';
import AutocompleteOrdersTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteOrdersTypeahead';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import Tabs from '~components/Tabs/Tabs';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { DriverDay } from '~hooks/useDriverDays/models';
import { useDriverDays } from '~hooks/useDriverDays/useDriverDays';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import {
  getLocationHistoriesByDriverDayId,
  getLocationHistoriesByJobId,
  getLocationHistoriesByLoadId,
  getLocationHistoriesByOrderId,
} from '~hooks/useLocationHistory/useLocationHistory';
import { Order } from '~hooks/useOrders/models';
import { useOrders } from '~hooks/useOrders/useOrders';
import { JobSiteUsage, LoadSiteUsage, OrderSiteUsage } from '~hooks/useSiteUsages/models';
import { ROUTES } from '~router/routePaths';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { buildEntitySelectPath, GeoModel } from './entitySelectPath';

type AdminGeoFormValues = {
  job: AutocompleteFieldOption<Job_Read_Typeahead> | null;
  order: AutocompleteFieldOption<Order_Read_Typeahead> | null;
  load: AutocompleteFieldOption<Load_Read_Typeahead> | null;
  driverDay: AutocompleteFieldOption<DriverDay_Read_Typeahead> | null;
};

const GEO_MODEL_TABS: { value: GeoModel; labelKey: string }[] = [
  { value: 'jobs', labelKey: 'admin.geo.jobs_radio_label' },
  { value: 'orders', labelKey: 'admin.geo.orders_radio_label' },
  { value: 'loads', labelKey: 'admin.geo.loads_radio_label' },
  { value: 'driver_days', labelKey: 'admin.geo.driver_days_radio_label' },
];

export const AdminGeo = () => {
  const { jobId, orderId, loadId, driverDayId } = useParams<{
    jobId?: string;
    orderId?: string;
    loadId?: string;
    driverDayId?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { getJobById } = useJob();
  const { getOrderById } = useOrders();
  const { getDriverDayById } = useDriverDays();
  const [fetchedJob, setFetchedJob] = useState<Job | null>(null);
  const [fetchedOrder, setFetchedOrder] = useState<Order | null>(null);
  const [fetchedDriverDay, setFetchedDriverDay] = useState<DriverDay | null>(null);
  const [fetchedLoadData, setFetchedLoadData] = useState<{
    travelledDistanceMeters: string | null;
  } | null>(null);
  const [fetchedSiteUsages, setFetchedSiteUsages] = useState<
    (JobSiteUsage | OrderSiteUsage | LoadSiteUsage)[]
  >([]);
  const [fetchedPings, setFetchedPings] = useState<LocationHistoryMinimal[]>([]);
  const [isMapDataLoading, setIsMapDataLoading] = useState(false);

  const { control, watch, setValue } = useForm<AdminGeoFormValues>({
    defaultValues: { job: null, order: null, load: null, driverDay: null },
  });

  const jobValue = watch('job');
  const orderValue = watch('order');
  const loadValue = watch('load');
  const driverDayValue = watch('driverDay');

  const getJobByIdRef = useRef(getJobById);
  const getOrderByIdRef = useRef(getOrderById);
  const getDriverDayByIdRef = useRef(getDriverDayById);
  getJobByIdRef.current = getJobById;
  getOrderByIdRef.current = getOrderById;
  getDriverDayByIdRef.current = getDriverDayById;

  const currentModel = useMemo((): GeoModel => {
    if (location.pathname.includes('/orders')) return 'orders';
    if (location.pathname.includes('/loads')) return 'loads';
    if (location.pathname.includes('/driver_days')) return 'driver_days';
    return 'jobs';
  }, [location.pathname]);

  const currentModelId = useMemo(() => {
    switch (currentModel) {
      case 'jobs':
        return jobId;
      case 'orders':
        return orderId;
      case 'loads':
        return loadId;
      case 'driver_days':
        return driverDayId;
      default:
        return undefined;
    }
  }, [currentModel, jobId, orderId, loadId, driverDayId]);

  const geoBasePath = `/${ROUTES.admin.base}/${ROUTES.admin.geo}`;

  const entityFieldByModel = useMemo(
    () =>
      ({
        jobs: 'job',
        orders: 'order',
        loads: 'load',
        driver_days: 'driverDay',
      }) as const,
    [],
  );

  useEffect(() => {
    if (!currentModelId || !companyId) {
      if (!currentModelId) {
        setValue(entityFieldByModel[currentModel], null);
      }
      setFetchedJob(null);
      setFetchedOrder(null);
      setFetchedDriverDay(null);
      setFetchedLoadData(null);
      setFetchedSiteUsages([]);
      setFetchedPings([]);
      return;
    }
    setFetchedJob(null);
    setFetchedOrder(null);
    setFetchedDriverDay(null);
    setFetchedLoadData(null);
    setFetchedSiteUsages([]);
    setFetchedPings([]);
    setIsMapDataLoading(true);
    const controller = new AbortController();
    const { signal } = controller;
    let siteUsagesPromise: Promise<void> = Promise.resolve();
    if (currentModel === 'jobs') {
      getJobByIdRef
        .current(currentModelId, companyId, signal)
        .then((job) => {
          if (signal.aborted || !job) return;
          setFetchedJob(job);
          setValue('job', {
            _entity: { id: job.id, job_id: job.jobId },
            label: job.jobId,
            value: job.jobId,
          });
        })
        .catch((error) => {
          if (signal.aborted) return;

          console.error('Failed to fetch job', error);
        });
      siteUsagesPromise = getV1CompaniesCompanyIdJobsJobIdSiteUsages({
        path: { 'company-id': companyId, 'job-id': currentModelId },
        signal,
      })
        .then((res) => {
          if (!signal.aborted)
            setFetchedSiteUsages((res.data.data ?? []).map(JobSiteUsage.parse));
        })
        .catch((error) => {
          if (signal.aborted) return;

          console.error('Failed to fetch site usages', error);
        });
    } else if (currentModel === 'orders') {
      getOrderByIdRef
        .current(currentModelId, signal)
        .then((order) => {
          if (signal.aborted || !order) return;
          setFetchedOrder(order);
          setValue('order', {
            _entity: { id: order.id, order_id: order.orderId },
            label: order.orderId,
            value: order.orderId,
          });
        })
        .catch((error) => {
          if (signal.aborted) return;

          console.error('Failed to fetch order', error);
        });
      siteUsagesPromise = getV1CompaniesCompanyIdOrdersOrderIdSiteUsages({
        path: { 'company-id': companyId, 'order-id': currentModelId },
        signal,
      })
        .then((res) => {
          if (!signal.aborted)
            setFetchedSiteUsages((res.data.data ?? []).map(OrderSiteUsage.parse));
        })
        .catch((error) => {
          if (signal.aborted) return;

          console.error('Failed to fetch site usages', error);
        });
    } else if (currentModel === 'loads') {
      getV1CompaniesCompanyIdLoadsId({
        path: { 'company-id': companyId, id: currentModelId },
        signal,
      })
        .then((res) => {
          if (signal.aborted || !res?.data?.data) return;
          const data = res.data.data;
          setFetchedLoadData({
            travelledDistanceMeters: data.load_summary?.travelled_distance_meters ?? null,
          });
          const { id, load_id } = data;
          setValue('load', { _entity: { id, load_id }, label: load_id, value: load_id });
        })
        .catch((error) => {
          if (signal.aborted) return;

          console.error('Failed to fetch load', error);
        });
      siteUsagesPromise = getV1CompaniesCompanyIdLoadsLoadIdSiteUsages({
        path: { 'company-id': companyId, 'load-id': currentModelId },
        signal,
      })
        .then((res) => {
          if (!signal.aborted)
            setFetchedSiteUsages((res.data.data ?? []).map(LoadSiteUsage.parse));
        })
        .catch((error) => {
          if (signal.aborted) return;

          console.error('Failed to fetch site usages', error);
        });
    } else if (currentModel === 'driver_days') {
      getDriverDayByIdRef
        .current(currentModelId, signal)
        .then((driverDay) => {
          if (signal.aborted || !driverDay) return;
          setFetchedDriverDay(driverDay);
          setValue('driverDay', {
            _entity: { id: driverDay.id, driver_day_id: driverDay.driverDayId },
            label: driverDay.driverDayId,
            value: driverDay.driverDayId,
          });
        })
        .catch((error) => {
          if (signal.aborted) return;

          console.error('Failed to fetch driver day', error);
        });
    }

    const pingsMap = {
      jobs: () => getLocationHistoriesByJobId(companyId, currentModelId, signal),
      orders: () => getLocationHistoriesByOrderId(companyId, currentModelId, signal),
      loads: () => getLocationHistoriesByLoadId(companyId, currentModelId, signal),
      driver_days: () =>
        getLocationHistoriesByDriverDayId(companyId, currentModelId, signal),
    } as const;
    const pingsPromise = pingsMap[currentModel]()
      .then((pings) => {
        if (!signal.aborted) setFetchedPings(pings);
      })
      .catch((error) => {
        if (signal.aborted) return;

        console.error('Failed to fetch location histories', error);
      });

    Promise.allSettled([siteUsagesPromise, pingsPromise]).then(() => {
      if (!signal.aborted) setIsMapDataLoading(false);
    });

    return () => controller.abort();
  }, [currentModelId, companyId, currentModel, setValue, entityFieldByModel]);

  const handleEntitySelect = useCallback(
    (selectedId: string) => {
      if (!selectedId || selectedId === currentModelId) return;
      navigate(
        buildEntitySelectPath({
          currentModel,
          pathname: location.pathname,
          selectedId,
          geoBasePath,
        }),
      );
    },
    [currentModelId, currentModel, geoBasePath, navigate, location.pathname],
  );

  useEffect(() => {
    const selectedId = jobValue?._entity?.id;
    if (currentModel === 'jobs' && selectedId) handleEntitySelect(selectedId);
  }, [jobValue?._entity?.id, currentModel, handleEntitySelect]);

  useEffect(() => {
    const selectedId = orderValue?._entity?.id;
    if (currentModel === 'orders' && selectedId) handleEntitySelect(selectedId);
  }, [orderValue?._entity?.id, currentModel, handleEntitySelect]);

  useEffect(() => {
    const selectedId = loadValue?._entity?.id;
    if (currentModel === 'loads' && selectedId) handleEntitySelect(selectedId);
  }, [loadValue?._entity?.id, currentModel, handleEntitySelect]);

  useEffect(() => {
    const selectedId = driverDayValue?._entity?.id;
    if (currentModel === 'driver_days' && selectedId) handleEntitySelect(selectedId);
  }, [driverDayValue?._entity?.id, currentModel, handleEntitySelect]);

  const isModelSelected = useMemo(() => {
    const modelPath = `${geoBasePath}/${currentModel}`;
    return (
      !!currentModelId ||
      location.pathname === modelPath ||
      location.pathname.startsWith(`${modelPath}/`)
    );
  }, [currentModelId, location.pathname, geoBasePath, currentModel]);

  const handleGeoTypeChange = useCallback(
    (value: string) => {
      navigate(`${geoBasePath}/${value}`);
    },
    [geoBasePath, navigate],
  );

  const handleJobSubTabChange = useCallback(
    (value: string) => {
      if (!jobId) return;
      const base = `${geoBasePath}/jobs/${jobId}`;
      navigate(`${base}/${value}`);
    },
    [jobId, geoBasePath, navigate],
  );

  const handleOtherModelSubTabChange = useCallback(() => {
    if (!currentModelId) return;
    const base = `${geoBasePath}/${currentModel}/${currentModelId}`;
    navigate(`${base}/distances`);
  }, [currentModelId, currentModel, geoBasePath, navigate]);
  const isOnGeofences = location.pathname.includes('/geofences');
  const isOnDistances = location.pathname.includes('/distances');
  const isOnAiSummary = location.pathname.includes('/ai_summary');

  const jobSubTabSelected = jobId
    ? isOnGeofences
      ? 'geofences'
      : isOnDistances
        ? 'distances'
        : isOnAiSummary
          ? 'ai_summary'
          : 'geofences'
    : 'geofences';

  const typeaheadBoxSx = {
    minWidth: 280,
    flex: '1 0 280px',
    maxWidth: 400,
    overflow: 'visible' as const,
    '& .MuiInputBase-root': {
      height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
      minHeight: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
    },
    '& .MuiInputBase-input': {
      py: 0,
      height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
      minHeight: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
      boxSizing: 'border-box',
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'nowrap',
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Tabs
            selected={currentModel}
            onChange={handleGeoTypeChange}
            tabs={GEO_MODEL_TABS.map((tab) => ({
              label: t(tab.labelKey),
              value: tab.value,
            }))}
          />
        </Box>
        {isModelSelected && (
          <>
            <Box sx={typeaheadBoxSx}>
              {currentModel === 'jobs' && (
                <AutocompleteJobsTypeahead<AdminGeoFormValues>
                  control={control}
                  name="job"
                  companyId={companyId ?? ''}
                />
              )}
              {currentModel === 'orders' && (
                <AutocompleteOrdersTypeahead<AdminGeoFormValues>
                  control={control}
                  name="order"
                  companyId={companyId ?? ''}
                />
              )}
              {currentModel === 'loads' && (
                <AutocompleteLoadsTypeahead<AdminGeoFormValues>
                  control={control}
                  name="load"
                  companyId={companyId ?? ''}
                />
              )}
              {currentModel === 'driver_days' && (
                <AutocompleteDriverDaysTypeahead<AdminGeoFormValues>
                  control={control}
                  name="driverDay"
                  companyId={companyId ?? ''}
                />
              )}
            </Box>
            {currentModelId && (
              <Box sx={{ flexShrink: 0 }}>
                <Tabs
                  selected={currentModel === 'jobs' ? jobSubTabSelected : 'distances'}
                  onChange={
                    currentModel === 'jobs'
                      ? handleJobSubTabChange
                      : handleOtherModelSubTabChange
                  }
                  tabs={
                    currentModel === 'jobs'
                      ? [
                          { label: t('admin.geo.geofences_tab'), value: 'geofences' },
                          { label: t('admin.geo.distances_tab'), value: 'distances' },
                          { label: t('admin.geo.ai_summary_tab'), value: 'ai_summary' },
                        ]
                      : [{ label: t('admin.geo.distances_tab'), value: 'distances' }]
                  }
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {currentModelId && (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Outlet
            context={{
              job: fetchedJob,
              order: fetchedOrder,
              driverDay: fetchedDriverDay,
              loadData: fetchedLoadData,
              siteUsages: fetchedSiteUsages,
              pings: fetchedPings,
              isMapDataLoading,
            }}
          />
        </Box>
      )}
    </Box>
  );
};
